import SiteInformation from "@/models/SiteInformation";
import Cadaster from "@/models/Cadaster";
import Section from "@/models/Section";
import { siteInformationClient } from "@/api/siteInformationClient";
import { cadasterApi, sectionApi } from "@/api/projectClient";
import { formatDecimal } from "@/utils/utils";
import { filtersToParams } from "@/utils/restMetadata";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useMissionStore } from "@/store/modules/mission";
import { validate as isUuid } from "uuid";

const SiteInformationRepo = useRepo(SiteInformation);
const CadasterRepo = useRepo(Cadaster);
const SectionRepo = useRepo(Section);

const normalizeSiteInformation = (siteInformation) => {
  const totalSurface = formatDecimal(siteInformation.totalSurface, 6, null);
  return {
    ...siteInformation,
    totalSurface: totalSurface === null ? null : Number(totalSurface),
  };
};

const normalizeCadaster = (cadaster) => ({
  ...cadaster,
  createdAt:
    cadaster.createdAt instanceof Date ? cadaster.createdAt.toISOString() : cadaster.createdAt,
  surface:
    cadaster.surface === null || cadaster.surface === undefined
      ? cadaster.surface
      : Number(cadaster.surface),
});

export const useProjectStore = defineStore("project", {
  state: () => ({
    sectionCount: 0,
  }),

  actions: {
    async fetchCurrentSiteInformation() {
      SiteInformationRepo.flush();
      const missionsStore = useMissionStore();
      const projectId = missionsStore.currentMissionUuid;
      const newSiteInformation = await siteInformationClient.getSiteInformationWithPlans(projectId);
      SiteInformationRepo.save(normalizeSiteInformation(newSiteInformation));
    },

    async fetchCadasters() {
      CadasterRepo.flush();
      const missionsStore = useMissionStore();
      const { results: cadasters } = await cadasterApi.list({
        project: missionsStore.currentMissionUuid,
        pageSize: 150,
      });
      if (!cadasters.length) {
        return null;
      }
      const normalizedCadasters = cadasters.map(normalizeCadaster);
      CadasterRepo.save(normalizedCadasters);
      return normalizedCadasters;
    },

    async fetchSections(params = {}) {
      SectionRepo.flush();
      const missionsStore = useMissionStore();
      const { results: sections, count } = await sectionApi.list({
        ...filtersToParams(params),
        project: missionsStore.currentMissionUuid,
      });
      if (!sections.length) {
        console.log("No cadaster found");
        return null;
      }
      this.setSectionCount(count);
      SectionRepo.save(sections);
    },

    async createOrUpdateCurrentSiteInformation(siteInformation) {
      const missionsStore = useMissionStore();
      siteInformation.project = missionsStore.currentMissionUuid;
      if (!siteInformation.uuid || !isUuid(siteInformation.uuid)) {
        const newSiteInformation = await siteInformationClient.create(siteInformation);
        if (newSiteInformation) {
          return SiteInformationRepo.insert(normalizeSiteInformation(newSiteInformation));
        }
      }
      const updatedSiteInformation = await siteInformationClient.partialUpdate(siteInformation);
      if (updatedSiteInformation) {
        return SiteInformationRepo.insert(normalizeSiteInformation(updatedSiteInformation));
      }
    },

    async partialUpdateSiteInformation({ siteInformation }) {
      const missionsStore = useMissionStore();
      const response = await siteInformationClient.partialUpdate({
        ...siteInformation,
        project: siteInformation.project || missionsStore.currentMissionUuid,
      });
      if (response) {
        SiteInformationRepo.insert(normalizeSiteInformation(response));
      }
    },

    async createOrUpdateCurrentCadaster(cadaster) {
      const missionsStore = useMissionStore();
      cadaster.project = missionsStore.currentMissionUuid;
      if (!cadaster.uuid || !isUuid(cadaster.uuid)) {
        const newCadaster = await cadasterApi.create({
          cadasterRequest: cadaster,
        });
        if (newCadaster) {
          const normalized = normalizeCadaster(newCadaster);
          CadasterRepo.insert(normalized);
          return normalized;
        }
      }
      const updatedCadaster = await cadasterApi.partialUpdate({
        uuid: cadaster.uuid,
        patchedCadasterRequest: cadaster,
      });
      if (updatedCadaster) {
        const normalized = normalizeCadaster(updatedCadaster);
        CadasterRepo.insert(normalized);
        return normalized;
      }
    },

    async createOrUpdateCurrentSection(section) {
      const missionsStore = useMissionStore();
      section.project = missionsStore.currentMissionUuid;
      if (!section.uuid || !isUuid(section.uuid)) {
        const newSection = await sectionApi.create({ sectionRequest: section });
        if (newSection) {
          return SectionRepo.insert(newSection);
        }
      }
      const updatedSection = await sectionApi.partialUpdate({
        uuid: section.uuid,
        patchedSectionRequest: section,
      });
      if (updatedSection) {
        return SectionRepo.insert(updatedSection);
      }
    },

    async deleteCurrentCadaster(uuid) {
      await cadasterApi.destroy({ uuid });
      return CadasterRepo.destroy(uuid);
    },

    async deleteCurrentSection(uuid) {
      await sectionApi.destroy({ uuid });
      return SectionRepo.destroy(uuid);
    },

    setSectionCount(count) {
      this.sectionCount = count;
    },
  },

  getters: {
    getCurrentSiteInformation: () => {
      return SiteInformationRepo.first();
    },
    getSections: () => {
      return SectionRepo.all();
    },
    getSectionsCount: (state) => state.sectionCount,
  },
});
